import { useMemo } from 'react';
import { Field } from '@common-fe/common-fe';

import { EMPTY_LABEL_SIGN, REQUIRED_TEXT } from '@/common/constants';
import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
// import { useAccountFundingParamsStore } from '@/modules/plan/PlanSetup/AccountFunding/AccountFundingParams/stores';
import { ROLLOVER_KEYS } from '@/modules/plan/PlanSetup/AccountFunding/Rollover/Rollover.constants';
import { GRACE_PERIOD_KEYS } from '@/modules/plan/PlanSetup/Coverage/GracePeriod/hooks/useGracePeriodFields';
import { useCurrentPlan } from '@/modules/plan/PlanSetup/hooks';
import { useAdditionalStore } from '@/modules/plan/PlanSetup/PlanDefinition/Additional/stores';
import { PLAN_SETUP_FIELD } from '@/modules/plan/PlanSetup/PlanSetup.constants';

import { PlanFieldState, PlanStatuses } from '../../plan.types';

import usePlanSetupFieldsState from './usePlanSetupFieldsState';

export interface StateFieldMap {
  [key: string]: PlanFieldState;
}

const CUSTOM_KEY = 'CUSTOM';
export const activeUpdateFields = [
  PLAN_SETUP_FIELD.PLAN_NAME,
  PLAN_SETUP_FIELD.PLAN_CODE,
  PLAN_SETUP_FIELD.PLAN_NOTES,
  PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.MINIMUM_ELECTION_AMOUNT,
  PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT_CUSTOM,
  PLAN_SETUP_FIELD.MAXIMUM_ELECTION_AMOUNT_IRS,
  PLAN_SETUP_FIELD.MINIMUM_ELECTION_AMOUNT_CUSTOM,
  PLAN_SETUP_FIELD.PROOF_OF_EXPENSE,
  PLAN_SETUP_FIELD.PROOF_OF_EXPENSES_CARD_TYPE,
  PLAN_SETUP_FIELD.GENERATE_STATEMENTS,
  PLAN_SETUP_FIELD.LU_GP_DESIGNATION_ALLOWED,
  PLAN_SETUP_FIELD.PLAN_LEVEL_FUNDING,
  PLAN_SETUP_FIELD.REPLENISHMENT_BANK_ACCOUNT,
  PLAN_SETUP_FIELD.REPLENISHMENT_FREQUENCY,
  PLAN_SETUP_FIELD.REPLENISHMENT_METHOD,
  PLAN_SETUP_FIELD.REPLENISHMENT_DAY_NUMBER,
  PLAN_SETUP_FIELD.SPEND_PERIOD,
  PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT,
  PLAN_SETUP_FIELD.PER_CATEGORY_SPEND_LIMIT,
  PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT,
  PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT_TYPE,
  PLAN_SETUP_FIELD.IS_PRE_FUND_CALCULATED,
  PLAN_SETUP_FIELD.IS_PARTICIPANT_PAID_MAINTENANCE_FEE,
  PLAN_SETUP_FIELD.MAINTENANCE_FEE,
  PLAN_SETUP_FIELD.APPLY_FEE_TO,
  PLAN_SETUP_FIELD.CASH_BALANCE_TRESHOLD,
  PLAN_SETUP_FIELD.CIP_ACCOUNT_CLOSURE_DELAY_DAYS,
  PLAN_SETUP_FIELD.IS_FORFEIT_ALLOWED,
  PLAN_SETUP_FIELD.FORFEITURE_DAYS,
  PLAN_SETUP_FIELD.PLAN_LEVEL_FUNDING,
  PLAN_SETUP_FIELD.EXTERNAL_BANK_ACCOUNT,
  PLAN_SETUP_FIELD.EXTERNAL_PUSH_RETURN_ACCOUNT,
  PLAN_SETUP_FIELD.BILLING_METHOD_TYPE_ID,

  PLAN_SETUP_FIELD.REPLENISHMENT_PERCENT,
  PLAN_SETUP_FIELD.REPLENISHMENT_MIN_VALUE,
  PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_PERCENT,
  PLAN_SETUP_FIELD.REPLENISHMENT_CREEP_MIN,
  PLAN_SETUP_FIELD.REPLENISHMENT_PERCENT_SPENDDOWN,
  PLAN_SETUP_FIELD.CONTRIBUTION_HOLD_PERIOD,
  PLAN_SETUP_FIELD.CONTRIBUTION_PUSH_GIVE_UP_DAYS,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION,
  PLAN_SETUP_FIELD.EOY_PLAN_RECONCILIATION_DELAY_DAYS,

  PLAN_SETUP_FIELD.INCLUDE_TO_BALANCE_REMINDER,
  PLAN_SETUP_FIELD.RETURN_FUNDS_AFTER_CIP_ACCOUNT_CLOSURE_TYPE,
  PLAN_SETUP_FIELD.PEND_CONTRIBUTIONS_FOR_CIP,
  PLAN_SETUP_FIELD.MINIMUM_INVESTMENT_AMOUNT,
  PLAN_SETUP_FIELD.INVESTMENT_THRESHOLD,

  ...Object.values(ROLLOVER_KEYS),
  ...Object.values(GRACE_PERIOD_KEYS || {}),
];

export const useActiveUpdateFields = () => {
  const sourceState = useAdditionalStore((state) => state.sourceState);
  return useMemo(() => {
    const additionalFields = [
      PLAN_SETUP_FIELD.IS_ALLOW_CONTRIBUTIONS_OVER_ELECTION_AMOUNT,
      PLAN_SETUP_FIELD.ALLOW_CONTRIBUTION_AFTER_ACCOUNT_CLOSURE,
    ];

    if (sourceState?.spendLimit !== CUSTOM_KEY) {
      additionalFields.push(
        PLAN_SETUP_FIELD.SPEND_LIMIT,
        PLAN_SETUP_FIELD.SPEND_LIMIT_IRS_AMOUNT,
        PLAN_SETUP_FIELD.SPEND_LIMIT_CUSTOM,
        PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT,
        PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT_TYPE
      );
    }
    return [...activeUpdateFields, ...additionalFields];
  }, [sourceState?.spendLimit]);
};

const usePlanFieldsState = (currentFields: Field[]) => {
  const fieldsState = usePlanSetupFieldsState();
  const { planStatus } = useCurrentPlan();
  const memoizedActiveUpdateFields = useActiveUpdateFields();

  const isViewHidden = useHasAccess([
    {
      permission: Permissions.VIEW_HIDDEN_PLAN_FIELDS,
    },
  ]);

  const states = fieldsState as StateFieldMap;
  const getFormatedFields = (fields: Field[], parentDisabled?: boolean) =>
    fields.reduce((result: Field[], field): Field[] => {
      const fieldState =
        field.name === PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT ||
        field.name === PLAN_SETUP_FIELD.PER_CATEGORY_SPEND_LIMIT
          ? (states[PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT] ?? states[field.name])
          : states[field.name];
      let currentField = {
        ...field,
      };
      if ((!field.label || field.label === EMPTY_LABEL_SIGN) && parentDisabled) {
        currentField = {
          ...currentField,
          disabled: true,
        };
      }
      const isDisabled = fieldState === PlanFieldState.Locked || fieldState === PlanFieldState.Hidden;
      if (field.subFields) {
        currentField = {
          ...currentField,
          subFields: getFormatedFields(field.subFields, isDisabled || currentField.disabled) as Field[],
        };
      }

      if (fieldState === PlanFieldState.Hidden) {
        if (isViewHidden) {
          return [
            ...result,
            {
              ...currentField,
              disabled: true,
            },
          ];
        }
        return result;
      }
      if (fieldState === PlanFieldState.Locked) {
        return [
          ...result,
          {
            ...currentField,
            disabled: true,
          },
        ];
      }
      if (fieldState === PlanFieldState.Modifiable) {
        if (field.disabled) {
          return [...result, currentField];
        } else {
          return [
            ...result,
            {
              ...currentField,
              disabled:
                (planStatus === PlanStatuses.ACTIVE || planStatus === PlanStatuses.PENDING_ACTIVE) &&
                !memoizedActiveUpdateFields.includes(field.name)
                  ? true
                  : false,
            },
          ];
        }
      }
      if (fieldState === PlanFieldState.RequiredAtPlan || fieldState === PlanFieldState.RequiredAtAllLevels) {
        return [
          ...result,
          {
            ...currentField,
            showRequireIcon: true,
            validator: currentField.validator?.required(REQUIRED_TEXT),
          },
        ];
      }
      return [...result, currentField];
    }, []);
  return getFormatedFields(currentFields);
};

export default usePlanFieldsState;
